<template>
  <portal to="identify-dialog-title">
    <img
      alt=""
      class="authority-service-logo"
      src="/svg/authority-services/did-logo.svg"
    />
  </portal>
  <div>
    <v-card class="mb-4 pa-4" color="grey-lighten-5" flat rounded="lg">
      <v-alert
        class="warning-message mb-4"
        color="warning"
        rounded
        variant="outlined"
      >
        <div class="d-flex flex-row align-center">
          <v-icon class="mr-2" color="warning" size="14"
            >fas fa-triangle-exclamation</v-icon
          >
          <div class="d-flex flex-column align-center">
            {{ i18n.t('dialogs.identify_warning.alert_text') }}
          </div>
        </div>
      </v-alert>
      <div
        v-if="
          authorityService === AuthorityService.MOBILE_ID &&
          jurisdiction === Jurisdiction.ZERTES
        "
        class="warning-hint"
      >
        {{
          i18n.t('dialogs.identify_warning.hint', {
            auth_service: i18n.t('authorities.sms_code')
          })
        }}
      </div>
      <div v-else class="warning-hint">
        {{
          i18n.t('dialogs.identify_warning.hint', {
            auth_service: i18n.t('authorities.ras')
          })
        }}
        <span class="warning-hint">
          <a
            class="text-primary"
            href="https://check-signature.scapp.swisscom.com/"
            rel="noopener"
            style="text-decoration: none"
            target="_blank"
          >
            {{ i18n.t('dialogs.identify_warning.check_phone_number') }}
          </a>
        </span>
      </div>
    </v-card>

    <template v-if="canUseDeepID">
      <div class="mb-3 warning-subtitle">
        {{ i18n.t('dialogs.identify_warning.subtitle') }}
      </div>

      <v-card class="deep-id-card" rounded="lg" variant="outlined">
        <!-- DISABLE EsLint here because of https://github.com/vuetifyjs/eslint-plugin-vuetify/issues/83 -->
        <!-- eslint-disable -->
        <v-img
          src="/svg/authority-services/did-logo.svg"
          position="left"
          class="authority-service-logo mb-4"
        />
        <!-- eslint-enable -->
        <div class="deep-id-overline">
          {{ i18n.t('dialogs.identify_warning.overline') }}
        </div>
        <div class="deep-id-text">
          {{ i18n.t('dialogs.identify_warning.deep_id_text') }}
        </div>
      </v-card>
    </template>

    <portal to="identify-dialog-actions">
      <VBtnSecondary
        :text="i18n.t('buttons.cancel')"
        variant="outlined"
        @click="closeDialog"
      />
      <VBtnPrimary
        v-if="canUseDeepID"
        color="primary"
        :text="i18n.t('buttons.continue_with_deep_id')"
        @click="continueWithDeepID"
      />
    </portal>
  </div>
</template>

<script setup lang="ts">
import AuthService from '@/services/authService.ts';
import { BetaFeature } from '@/types/enums/BetaFeature';
import { computed, type PropType } from 'vue';
import { useEmitter } from '@/composables/useEmitter';
import { useI18n } from 'vue-i18n';
import { AuthorityService } from '@/types/enums/AuthorityService';
import { useSignContext } from '@/composables/useSignContext';
import { Jurisdiction } from '@/types/enums/Jurisdiction';

const emit = defineEmits(['close-dialog']);

defineProps({
  authorityService: {
    type: String as PropType<AuthorityService>,
    default: ''
  }
});

const emitter = useEmitter();

const i18n = useI18n();

const { jurisdiction } = useSignContext();

const canUseDeepID = computed<boolean>(() => {
  return (
    import.meta.env.VITE_AUTHORITY_SERVICE_ENABLED === 'true' ||
    AuthService.hasBetaFeature(BetaFeature.DEEP_ID_SIGN)
  );
});

function closeDialog() {
  emit('close-dialog');
}

function continueWithDeepID() {
  emitter.$emit('set-authority-service', 'did');
  closeDialog();
}
</script>

<style scoped lang="scss">
.authority-service-logo {
  height: 20px;
}

.card-title {
  font-weight: 600;
  font-size: 22px !important;
  line-height: 28px !important;
  margin-bottom: 24px;
  white-space: normal;
}

.warning-message {
  background-color: white !important;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  padding: 12px;
}

.deep-id-card {
  padding: 16px;
  background-color: white;
  border-color: rgb(var(--v-theme-primary)) !important;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15) !important;
}

.warning-hint {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: black;
}

.warning-subtitle {
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: black;
}

.deep-id-overline {
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
  color: black;
  margin-bottom: 8px;
}

.deep-id-text {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: black;
}
</style>
